import { useState } from 'react';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useAppStore } from '@/lib/store';
import { useCurrencyRates } from '@/hooks/useCurrency';
import { useCurrencyHistory, calculatePercentageChange, formatRate } from '@/hooks/useCurrencyHistory';
import type { Currency } from '@/types';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';
import SimpleLineChart from '@/components/SimpleLineChart';

// Available currencies to choose as base currency
// ISK is always the target currency
const BASE_CURRENCIES: Currency[] = ['USD', 'EUR', 'GBP', 'DKK', 'NOK', 'SEK', 'PLN', 'TRY'];
const TARGET_CURRENCY: Currency = 'ISK';

export default function CurrencyPage() {
  const { language } = useAppLanguage();
  const userCurrency = useAppStore((state) => state.currency);
  const [selectedBaseCurrency, setSelectedBaseCurrency] = useState<Currency>(
    BASE_CURRENCIES.includes(userCurrency) ? userCurrency : 'EUR'
  );
  const [showFullHistory, setShowFullHistory] = useState(false);

  const ratesQuery = useCurrencyRates(selectedBaseCurrency);
  const historyQuery = useCurrencyHistory(selectedBaseCurrency, TARGET_CURRENCY);

  if (ratesQuery.isLoading) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('currencyRate', language)} />
        <div style={styles.content}>
          <div style={styles.loadingMessage}>Loading exchange rates...</div>
        </div>
      </DetailPageLayout>
    );
  }

  if (ratesQuery.isError) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('currencyRate', language)} />
        <div style={styles.content}>
          <div style={styles.errorMessage}>
            Unable to load exchange rates. Please try again later.
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  const rates = (ratesQuery.data?.rates || {}) as Record<Currency, number>;
  const currentRate = rates[TARGET_CURRENCY];
  const history = historyQuery.data?.history || [];
  const percentChange = history.length >= 2
    ? calculatePercentageChange(history[0].rate, history[history.length - 1].rate)
    : 0;

  return (
    <DetailPageLayout>
      <DetailPageHeader title={getTranslation('currencyRate', language)} />

      <div style={styles.content}>
        {/* Current Rate Card */}
        <div style={styles.currentRateCard}>
          <div style={styles.rateDisplayTop}>
            <div style={styles.rateAmount}>
              <span style={styles.rateLabel}>1</span>
              <span style={styles.rateCurrency}>{selectedBaseCurrency}</span>
              <span style={styles.rateEquals}>=</span>
              <span style={styles.rateValue}>{currentRate ? currentRate.toFixed(2) : '—'}</span>
              <span style={styles.rateCurrency}>{TARGET_CURRENCY}</span>
            </div>
            {percentChange !== 0 && (
              <div style={{
                ...styles.percentChange,
                color: percentChange > 0 ? '#4caf50' : '#d32f2f',
              }}>
                {percentChange > 0 ? '↑' : '↓'} {Math.abs(percentChange).toFixed(2)}%
              </div>
            )}
          </div>

          {/* Base Currency Selector */}
          <div style={styles.currencySelector}>
            {BASE_CURRENCIES.map((curr) => (
              <button
                key={curr}
                onClick={() => setSelectedBaseCurrency(curr)}
                style={{
                  ...styles.currencyButton,
                  ...(selectedBaseCurrency === curr ? styles.currencyButtonActive : {}),
                }}
              >
                {curr}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        {!historyQuery.isLoading && history.length > 0 && (
          <SimpleLineChart data={history} />
        )}

        {/* History Table */}
        <div style={styles.section}>
          <div style={styles.historyHeaderContainer}>
            <h2 style={styles.sectionTitle}>30-Day History</h2>
            <button
              onClick={() => setShowFullHistory(!showFullHistory)}
              style={{
                ...styles.toggleButton,
                ...(showFullHistory ? styles.toggleButtonActive : {}),
              }}
            >
              {showFullHistory ? 'Show Less' : 'Show More'}
            </button>
          </div>
          <div style={styles.historyTable}>
            <div style={styles.historyHeader}>
              <div style={styles.historyCell}>Date</div>
              <div style={styles.historyCell}>Rate</div>
              <div style={styles.historyCell}>Change</div>
            </div>
            {(showFullHistory ? [...history].reverse() : [...history].reverse().slice(0, 7)).map((item, index) => {
              const displayHistory = showFullHistory ? [...history].reverse() : [...history].reverse().slice(0, 7);
              const prevRate = index > 0 ? displayHistory[index - 1].rate : item.rate;
              const change = calculatePercentageChange(prevRate, item.rate);
              const date = new Date(item.date);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

              return (
                <div key={item.date} style={styles.historyRow}>
                  <div style={styles.historyCell}>{dateStr}</div>
                  <div style={styles.historyCell}>{formatRate(item.rate)}</div>
                  <div style={{
                    ...styles.historyCell,
                    color: change > 0 ? '#4caf50' : change < 0 ? '#d32f2f' : Colors.textSecondary,
                  }}>
                    {change > 0 ? '+' : ''}{change.toFixed(2)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <div style={{ height: '40px' }} />
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    padding: '24px',
  } as React.CSSProperties,

  loadingMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: Colors.textSecondary,
    fontSize: '16px',
  } as React.CSSProperties,

  errorMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: '#d32f2f',
    fontSize: '16px',
  } as React.CSSProperties,

  noRatesMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: Colors.textSecondary,
    fontSize: '14px',
  } as React.CSSProperties,

  section: {
    marginBottom: '32px',
  } as React.CSSProperties,

  historyHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: Colors.text,
    marginBottom: '16px',
    margin: 0,
  } as React.CSSProperties,

  toggleButton: {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '600',
    color: Colors.primary,
    backgroundColor: Colors.primaryLight,
    border: `1px solid ${Colors.primary}`,
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  toggleButtonActive: {
    backgroundColor: Colors.primary,
    color: '#fff',
  } as React.CSSProperties,

  rateItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: Colors.surface,
    borderRadius: '12px',
    marginBottom: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  rateCode: {
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.primary,
  } as React.CSSProperties,

  rateValueSmall: {
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.text,
  } as React.CSSProperties,

  currentRateCard: {
    backgroundColor: Colors.surface,
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  rateDisplayTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  } as React.CSSProperties,

  rateAmount: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
  } as React.CSSProperties,

  rateLabel: {
    fontSize: '16px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  rateCurrency: {
    fontSize: '16px',
    fontWeight: '600',
    color: Colors.primary,
  } as React.CSSProperties,

  rateEquals: {
    fontSize: '16px',
    color: Colors.textSecondary,
    margin: '0 4px',
  } as React.CSSProperties,

  rateValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: Colors.primary,
  } as React.CSSProperties,

  percentChange: {
    fontSize: '12px',
    fontWeight: '500',
    padding: '4px 8px',
    borderRadius: '4px',
  } as React.CSSProperties,

  currencySelector: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  currencyButton: {
    padding: '8px 16px',
    backgroundColor: Colors.primaryLight,
    border: `1px solid ${Colors.primary}`,
    borderRadius: '8px',
    color: Colors.primary,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  currencyButtonActive: {
    backgroundColor: Colors.primary,
    color: '#fff',
  } as React.CSSProperties,

  historyTable: {
    backgroundColor: Colors.surface,
    borderRadius: '12px',
    overflow: 'hidden',
  } as React.CSSProperties,

  historyHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px',
    padding: '16px',
    backgroundColor: Colors.primaryLight,
    borderBottom: `1px solid ${Colors.surface}`,
    fontWeight: '600',
    color: Colors.primary,
  } as React.CSSProperties,

  historyRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: `1px solid ${Colors.surface}`,
    alignItems: 'center',
  } as React.CSSProperties,

  historyCell: {
    fontSize: '14px',
    color: Colors.text,
  } as React.CSSProperties,

  ratesList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '12px',
  } as React.CSSProperties,
} as const;
